import { useState, useEffect } from "react";

import {
  Apple,
  ArrowRight,
  Book,
  BookMarked,
  BookOpenText,
  ClipboardList,
  Clock,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Globe,
  GraduationCap,
  Joystick,
  LaptopMinimal,
  Map,
  Microscope,
  Pencil,
  PersonStanding,
  Pi,
  Play,
  SquareArrowOutUpRight,
  Users
} from "lucide-react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Badge, Button, Spinner } from "reactstrap";
import { bindActionCreators } from "redux";

import bannerImage from "./aluno.jpeg";
import { logout } from "../../../actions/baseActions";
import URL from "../../../config/api";
import axios from "../../../config/axios";
import { estaConcluida } from "../../../utils/atividadesPendentes";
import { friendlyDate } from "../../../utils/date";
import { formatString } from "../../../utils/string";
import { ContentCard } from "../../ContentCard";

import "./styles.css";

function NewStudentDashboard({ ...props }) {
  const [contents, setContents] = useState();
  const [activities, setActivities] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerRow, setItemsPerRow] = useState(6);

  useEffect(() => {
    pullDashboardData();
    pullContent();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerRow(2);
      } else if (window.innerWidth < 992) {
        setItemsPerRow(3);
      } else if (window.innerWidth < 1200) {
        setItemsPerRow(4);
      } else {
        setItemsPerRow(6);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once to set initial state

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const pullDashboardData = async () => {
    try {
      const dashboardResp = await axios.get(
        `${URL.api}general/dashboard/user`,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const parsedActivities = dashboardResp.data.data.activities.splice(0, 4);
      setActivities(parsedActivities);
    } catch (error) {
      toastr.error("Erro", "Erro ao buscar conteúdos");
    }
  };

  const pullContent = async () => {
    try {
      const gamesObj = {
        ability: "null",
        content: true,
        private: false,
        type: "2"
      };

      const losResp = await axios.post(
        `${URL.api}content/los/listLosFilterMenu?page=1&page_size=12`,
        gamesObj,
        {
          headers: { Authorization: `Bearer ${props.user.token}` }
        }
      );

      const contentList = losResp.data.data.reduce((acc, lo) => {
        const typedContent = lo.content.map(content => ({
          ...content,
          type: lo.name,
          action: [
            {
              title: "Abrir conteúdo",
              icon: <SquareArrowOutUpRight size={14} />,
              action: () => {
                props.history.push(`/content/view/${content.id}`);
              }
            }
          ]
        }));

        return [...acc, ...typedContent];
      }, []);

      setContents(contentList);
    } catch (error) {
      toastr.error("Erro", "Erro ao buscar conteúdos");
    }
  };

  const handleActivityIcon = (disciplineName, color = "white", size = 24) => {
    switch (disciplineName) {
      case "Matemática":
        return <Pi size={size} className={`text-${color}`} />;
      case "Literatura":
        return <Book size={size} className={`text-${color}`} />;
      case "Português":
        return <Pencil size={size} className={`text-${color}`} />;
      case "Ciências":
        return <Microscope size={size} className={`text-${color}`} />;
      case "Física":
        return <Apple size={size} className={`text-${color}`} />;
      case "Geografia":
        return <Globe size={size} className={`text-${color}`} />;
      case "História":
        return <BookOpenText size={size} className={`text-${color}`} />;
      case "Educação Física":
        return <PersonStanding size={size} className={`text-${color}`} />;
      case "Religião":
        return <BookMarked size={size} className={`text-${color}`} />;
      case "Tecnologias da Informação e Comunicação":
        return <LaptopMinimal size={size} className={`text-${color}`} />;

      default:
        return <GraduationCap size={size} className={`text-${color}`} />;
    }
  };

  const handleActivityColor = disciplineName => {
    switch (disciplineName) {
      case "Matemática":
        return "info";
      case "Literatura":
        return "warning";
      case "Português":
        return "orange";
      case "Ciências":
        return "lime";
      case "Física":
        return "orange";
      case "Geografia":
        return "danger";
      case "História":
        return "primary";
      case "Educação Física":
        return "warning";
      case "Religião":
        return "danger";
      case "Tecnologias da Informação e Comunicação":
        return "purple";

      default:
        return "info";
    }
  };

  const hasContentImage = content =>
    content.image && content.image.includes(".educandus.com.br/");

  const handleSplicedDate = date => {
    const newDate = friendlyDate(date);
    return newDate.substring(0, newDate.indexOf(", às"));
  };

  const checkIfContentIsNew = content => {
    const today = new Date();
    const contentDate = new Date(content.updated_at);
    const diffTime = Math.abs(today - contentDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays <= 30;
  };

  const handlePrevClick = () => {
    setCurrentIndex(prevIndex => Math.max(prevIndex - itemsPerRow, 0));
  };

  const handleNextClick = () => {
    setCurrentIndex(prevIndex =>
      Math.min(prevIndex + itemsPerRow, contents.length - itemsPerRow)
    );
  };

  return (
    <div className="container">
      <section className="mb-5 d-flex flex-column">
        <h3 className="m-0 font-weight-bold">Olá, {props.user.user.name} 👋</h3>
        <h5 className="text-muted m-0">Vamos aprender algo novo?</h5>
      </section>

      <div className="student-dashboard-main-content mb-5">
        <div className="student-dashboard-shortcut-header d-flex align-items-center">
          <h5 className="m-0 font-weight-bold">Mais acessados</h5>
        </div>
        <section className="student-dashboard-shortucut-section">
          <div className="student-dashboard-main-grid">
            <div className="student-dashboard-banner d-flex align-items-center justify-content-center rounded overflow-hidden">
              <a href="/adr/external-login">
                <img
                  src={bannerImage}
                  alt="banner"
                  className="h-100 w-100 position-relative"
                  style={{
                    objectFit: "cover"
                  }}
                />
              </a>
            </div>
            <a
              href="/classroom/me"
              className="text-decoration-none rounded shadow-sm bg-white border flex-column align-items-start justify-content-between p-2 d-flex"
            >
              <div
                className="d-flex align-items-center justify-content-center p-2 rounded-circle mb-2"
                style={{ backgroundColor: "#6F42C133" }}
              >
                <Users size={18} className="text-purple" />
              </div>
              <p className="m-0 text-dark font-weight-semibold">
                Minhas turmas
              </p>
            </a>

            <a
              href="/content/list/2"
              className="text-decoration-none rounded shadow-sm bg-white border flex-column align-items-start justify-content-between p-2 d-flex"
            >
              <div
                className="d-flex align-items-center justify-content-center p-2 rounded-circle mb-2"
                style={{ backgroundColor: "#F48C5133" }}
              >
                <Joystick size={18} className="text-warning" />
              </div>
              <p className="m-0 text-dark font-weight-semibold">Jogos</p>
            </a>

            <a
              href="/content/list/9"
              className="text-decoration-none rounded shadow-sm bg-white border flex-column align-items-start justify-content-between p-2 d-flex"
            >
              <div
                className="d-flex align-items-center justify-content-center p-2 rounded-circle mb-2"
                style={{ backgroundColor: "#97CA5233" }}
              >
                <Book size={18} className="text-lime" />
              </div>
              <p className="m-0 text-dark font-weight-semibold">Livros</p>
            </a>

            <a
              href="/content/list/4"
              className="text-decoration-none rounded shadow-sm bg-white border flex-column align-items-start justify-content-between p-2 d-flex"
            >
              <div
                className="d-flex align-items-center justify-content-center p-2 rounded-circle mb-2"
                style={{ backgroundColor: "#E3676733" }}
              >
                <Play size={18} className="text-danger" />
              </div>
              <p className="m-0 text-dark font-weight-semibold">Animações</p>
            </a>

            <a
              href="/adr/external-login"
              className="text-decoration-none rounded shadow-sm bg-white border flex-column align-items-start justify-content-between p-2 d-none d-md-flex "
            >
              <div
                className="d-flex align-items-center justify-content-center p-2 rounded-circle mb-2"
                style={{ backgroundColor: "#02517D33" }}
              >
                <ClipboardList size={18} className="text-primary" />
              </div>
              <p className="m-0 text-dark font-weight-semibold">
                Avaliando Conhecimentos
              </p>
            </a>
          </div>
        </section>

        <div className="student-dashboard-activities-header d-flex align-items-center justify-content-between m-0">
          <h5 className="font-weight-bold m-0">Atividades</h5>
          {activities && activities.length > 0 && (
            <a
              href={
                activities && activities.length > 0
                  ? `/classroom/${activities[0].room_id}/activities`
                  : "/"
              }
              className="border rounded d-flex align-items-center justify-content-center p-2 font-weight-semibold text-muted bg-white text-decoration-none"
              style={{ fontSize: "12px" }}
            >
              Ver todas
              <ArrowRight size={16} className="ml-1 d-none d-md-block" />
            </a>
          )}
        </div>

        <section className="student-dashboard-pending-activities w-100">
          {!activities ? (
            <div className="d-flex align-items-center justify-content-center w-100 h-100 text-muted">
              <Spinner color="secondary" size="sm" className="mr-1" />
              Carregando atividades...
            </div>
          ) : activities.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center w-100 h-100 text-muted">
              Não há atividades pendentes no momento.
            </div>
          ) : (
            <ul className="d-flex flex-column list-unstyled p-0 m-0 h-100">
              {activities.map(activity => (
                <a
                  key={activity.id}
                  href={`/activity/do/${
                    activity.trail_allocation_id
                      ? activity.trail_allocation_id
                      : activity.allocation_id
                  }`}
                  className="w-100 bg-white border rounded d-flex align-items-center h-100 text-decoration-none overflow-hidden shadow-sm"
                >
                  <div
                    style={{ aspectRatio: "1/1" }}
                    className={`px-3 py-3 py-xl-0 h-100 mr-3 d-flex align-items-center justify-content-center bg-${handleActivityColor(
                      activity.discipline_name
                    )}`}
                  >
                    {handleActivityIcon(activity.discipline_name, "white", 24)}
                  </div>
                  <div className="d-flex flex-column w-100">
                    <div className="d-flex align-items-center w-100 mb-2">
                      {/* `text-truncate` em vez de `text-nowrap`: o nome tem de
                          CEDER espaço aos selos. Com nowrap ele empurrava os
                          selos para fora do cartão (que é overflow-hidden), e
                          quem sumia primeiro era justamente o "Concluída" das
                          atividades de nome longo. O `title` continua inteiro. */}
                      <p
                        className="font-weight-normal m-0 text-truncate text-dark"
                        title={activity.name}
                        style={{ minWidth: "0" }}
                      >
                        {activity.name}
                      </p>
                      {activity.homework && (
                        <Badge color="info" className="ml-2 flex-shrink-0">
                          Casa
                        </Badge>
                      )}
                      {estaConcluida(activity) && (
                        <Badge
                          color="success"
                          className="ml-2 flex-shrink-0 d-flex align-items-center"
                        >
                          <CircleCheck size={12} className="mr-1" />
                          Concluída
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex align-itens-center">
                      <small className="text-muted d-none d-md-flex text-nowrap align-items-center mr-2">
                        <ClipboardList size={12} className="mr-1" />
                        {activity.items.length} itens
                      </small>
                      <small className="text-muted d-flex align-items-center text-nowrap mr-2">
                        <Clock size={12} className="mr-1" />
                        até {handleSplicedDate(activity.activity_end)}
                      </small>
                      <small
                        className="text-muted text-truncate d-none d-md-inline-block"
                        title={activity.discipline_name}
                        style={{ minWidth: "0" }}
                      >
                        <Book size={12} className="mr-1" />
                        {formatString(activity.discipline_name, 12)}
                      </small>
                    </div>
                  </div>
                </a>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="d-flex flex-column m-0 mb-5">
        <div className="d-inline-flex justify-content-between">
          <h5 className="font-weight-bold m-0">Conteúdos</h5>
          <div>
            <Button
              onClick={handlePrevClick}
              disabled={currentIndex === 0}
              className="rounded-circle btn-sm bg-white mr-2"
            >
              <ChevronLeft size={18} />
            </Button>
            <Button
              onClick={handleNextClick}
              disabled={
                !contents || currentIndex >= contents.length - itemsPerRow
              }
              className="rounded-circle btn-sm bg-white"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>

        {!contents ? (
          <div
            className="d-flex align-items-center justify-content-center w-100 rounded mt-3 text-muted"
            style={{ minHeight: "200px" }}
          >
            <Spinner color="secondary" size="sm" className="mr-1" />
            Carregando conteúdos...
          </div>
        ) : contents.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center w-100 rounded mt-3 text-muted"
            style={{ minHeight: "320px" }}
          >
            Não há conteúdos disponíveis no momento.
          </div>
        ) : (
          <div className="row">
            {contents
              .slice(currentIndex, currentIndex + itemsPerRow)
              .map(content => (
                <div className="col-12 col-sm-6 col-lg-4 mb-3" key={content.id}>
                  <ContentCard.Root
                    titleLink={`/content/view/${content.id}?title=${content.name}`}
                  >
                    <ContentCard.Banner
                      fallbackColor={handleActivityColor(
                        content.discipline_name
                      )}
                      fallbackIcon={handleActivityIcon(content.discipline_name)}
                      // actions={content.action}
                      badge={checkIfContentIsNew(content) && "Novo"}
                      image={hasContentImage(content) && content.image}
                      height="108px"
                    />
                    <ContentCard.Main>
                      <ContentCard.Header title={content.name}>
                        <ContentCard.Item>
                          <small>{content.discipline_name}</small>
                        </ContentCard.Item>
                        {/* <ContentCard.Item>
                          <small>
                            {content.ability_code &&
                              ` • (${content.ability_code})`}
                          </small>
                        </ContentCard.Item> */}
                      </ContentCard.Header>
                    </ContentCard.Main>
                  </ContentCard.Root>
                </div>
              ))}
          </div>
        )}
      </section>
    </div>
  );
}

const mapStateToProps = state => ({
  user: state.user,
  general: state.general,
  profile: state.profile,
  form: state.form
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      logout
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NewStudentDashboard);
